import { useState, useEffect } from 'react';
import { C, useTheme } from '../lib/theme';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { PAGE_BY_ROUTE } from '../lib/pages';
import { useI18n } from '../lib/i18n';
import { useYear } from '../lib/useYear';
import { LayoutDashboard, Calendar, Building2, Clock, LogOut, Menu, X, ClipboardList, Users, Settings, Sigma, TrendingUp, PiggyBank, HelpCircle, Sun, Moon } from 'lucide-react';

/** Logo que acompanha o tema: branco no escuro, navy no claro. */
const logoSrc = (mode: 'dark' | 'light') =>
  mode === 'light' ? '/gri-institute-logo.svg' : '/gri-institute-logo-white.svg';

/** Botão sol/lua — alterna o tema (persistido em localStorage). */
function ThemeToggle() {
  const { mode, toggle } = useTheme();
  const { t } = useI18n();
  const isLight = mode === 'light';
  return (
    <button
      onClick={toggle}
      title={isLight ? t('theme_to_dark') : t('theme_to_light')}
      aria-label={isLight ? t('theme_to_dark') : t('theme_to_light')}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, flexShrink: 0, borderRadius: 8,
        border: `1px solid ${C.border}`, background: 'transparent',
        color: C.soft, cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
      }}
    >
      {isLight ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  );
}
import { TourProvider, useTourControls } from '../lib/useTour';

const navItems = [
  { to: '/', icon: LayoutDashboard, labelKey: 'nav_dashboard' as const },
  { to: '/somatoria', icon: Sigma, labelKey: 'nav_somatoria' as const, requiresRole: true },
  { to: '/events', icon: Calendar, labelKey: 'nav_events' as const, requiresRole: true },
  { to: '/institute', icon: Building2, labelKey: 'nav_institute' as const, requiresRole: true },
  { to: '/timeline', icon: Clock, labelKey: 'nav_timeline' as const, requiresRole: true },
  { to: '/manage-events', icon: Settings, labelKey: 'nav_manage' as const, adminOnly: true },
  { to: '/fx-rates', icon: TrendingUp, labelKey: 'nav_fx_rates' as const, financeOnly: true },
  { to: '/budgets', icon: PiggyBank, labelKey: 'nav_budgets' as const, adminOnly: true },
  { to: '/users', icon: Users, labelKey: 'nav_users' as const, adminOnly: true },
  { to: '/audit', icon: ClipboardList, labelKey: 'nav_audit' as const, adminOnly: true },
];

function LangToggle({ onLogout }: { onLogout: () => void }) {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={t('settings_title')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 8,
          border: `1px solid ${open ? '#c9a84c' : '#2a3f5a'}`,
          background: open ? C.activeBg : 'transparent',
          color: open ? C.activeInk : '#6a8aaa',
          cursor: 'pointer', transition: 'all 0.15s', fontSize: 15,
        }}
      >
        ⚙️
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
          {/* Dropdown */}
          <div style={{
            position: 'absolute', bottom: 36, right: 0, zIndex: 100,
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: '8px 0', minWidth: 180,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '4px 14px 8px' }}>
              {t('settings_title')}
            </div>
            <div style={{ height: 1, background: C.border, marginBottom: 6 }} />
            <div style={{ padding: '2px 8px' }}>
              <div style={{ fontSize: 11, color: C.muted, padding: '2px 6px', marginBottom: 4 }}>{t('settings_language')}</div>
              {(['pt', 'en'] as const).map(l => (
                <button key={l} onClick={() => { setLang(l); setOpen(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 10px', borderRadius: 6, border: 'none',
                  background: lang === l ? C.activeBg : 'transparent',
                  color: lang === l ? C.activeInk : C.muted,
                  cursor: 'pointer', fontSize: 13, fontWeight: lang === l ? 600 : 400,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  <span>{l === 'pt' ? '🇧🇷' : '🇬🇧'}</span>
                  <span>{l === 'pt' ? 'Português' : 'English'}</span>
                  {lang === l && <span style={{ marginLeft: 'auto', color: C.goldText, fontSize: 12 }}>✓</span>}
                </button>
              ))}
            </div>
            <div style={{ height: 1, background: C.border, margin: '6px 0' }} />
            <div style={{ padding: '2px 8px' }}>
              <button onClick={() => { setOpen(false); onLogout(); }} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '7px 10px', borderRadius: 6, border: 'none',
                background: 'transparent', color: C.redSoft,
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
              }}>
                <LogOut size={14} />
                <span>{t('logout_confirm')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

function LogoutModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.15s ease',
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        @keyframes slideInLeft { from { transform: translateX(-100%) } to { transform: translateX(0) } }
        .logout-btn-cancel:hover { background: ${C.border} !important; color: #fff !important; }
        .logout-btn-confirm:hover { background: #b33a3a !important; }
      `}</style>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 20, padding: '36px 32px', width: 380, maxWidth: 'calc(100vw - 32px)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        animation: 'slideUp 0.18s ease',
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(220, 74, 74, 0.12)',
          border: '1.5px solid rgba(220, 74, 74, 0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20,
        }}>
          <LogOut size={26} color={C.redSoft} />
        </div>

        <div style={{ fontSize: 20, fontWeight: 700, color: C.white, letterSpacing: '-0.3px', marginBottom: 10 }}>
          {t('logout_title')}
        </div>

        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 28 }}>
          {t('logout_body')}
        </div>

        <div style={{ width: '100%', height: 1, background: C.border, marginBottom: 24 }} />

        <div style={{ display: 'flex', gap: 12, width: '100%' }}>
          <button className="logout-btn-cancel" onClick={onCancel} style={{
            flex: 1, padding: '12px 0', borderRadius: 10, border: `1px solid ${C.border}`,
            background: 'transparent', color: C.muted, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s',
          }}>{t('logout_cancel')}</button>
          <button className="logout-btn-confirm" onClick={onConfirm} style={{
            flex: 1, padding: '12px 0', borderRadius: 10, border: 'none',
            background: '#c0392b', color: '#fff', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', transition: 'background 0.15s',
          }}>{t('logout_confirm')}</button>
        </div>
      </div>
    </div>
  );
}


function YearSelector() {
  const { selectedYear, setSelectedYear, availableYears, loading } = useYear();
  const safeYears = Array.isArray(availableYears) ? availableYears : [];
  if (loading) return null;

  return (
    <div data-tour="year-selector" style={{ padding: '8px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Ano</span>
      <select
        value={selectedYear}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedYear(Number(e.target.value))}
        style={{
          flex: 1, background: C.deep, border: `1px solid ${C.border}`, borderRadius: 6,
          color: C.goldText, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          padding: '3px 6px', outline: 'none', fontFamily: "'Inter', sans-serif",
          colorScheme: 'dark',
        }}
      >
        {safeYears.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}

function TourButton({ compact = false }: { compact?: boolean }) {
  const { available, start } = useTourControls();
  const { t } = useI18n();
  if (!available) return null;
  if (compact) {
    // Ícone-só no fim do item de menu ativo; não navega (para o clique no NavLink).
    return (
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); start(); }}
        title={t('tour_start')}
        aria-label={t('tour_start')}
        style={{
          marginLeft: 'auto', width: 24, height: 24, borderRadius: 6, flexShrink: 0,
          border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <HelpCircle size={14} />
      </button>
    );
  }
  return (
    <button onClick={start} title={t('tour_start')} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
      padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`,
      background: 'transparent', color: C.soft, cursor: 'pointer',
      fontSize: 12, fontWeight: 600, fontFamily: "'Inter', sans-serif",
    }}>
      <HelpCircle size={15} />
      <span>{t('tour_start')}</span>
    </button>
  );
}

function SidebarContent({ user, onLogout, onNavClick }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any;
  onLogout: () => void;
  onNavClick?: () => void;
}) {
  const { lang, t } = useI18n();
  const { mode } = useTheme();
  return (
    <>
      {/* Logo */}
      <div style={{ padding: '20px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          <img src={logoSrc(mode)} alt="GRI Institute" style={{ height: 28, width: 'auto', display: 'block' }} />
          <div style={{ fontSize: 10, fontWeight: 600, color: C.goldText, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('app_title')}</div>
        </div>
        <ThemeToggle />
      </div>

      <YearSelector />

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {(() => {
          const navItemsWithLabels = navItems.map(item => ({ ...item, label: t(item.labelKey) }));
          // Visibilidade por páginas efetivas do usuário (vindas do /me).
          const allowed = user?.allowedPages ?? [];
          const visible = navItemsWithLabels.filter(item => {
            const page = PAGE_BY_ROUTE[item.to];
            return page ? allowed.includes(page.key) : true;
          });

          const groups = [
            { label: lang === 'en' ? 'Overview' : 'Visão', items: visible.filter(i => ['/', '/somatoria'].includes(i.to)) },
            { label: lang === 'en' ? 'Planning' : 'Planejamento', items: visible.filter(i => ['/events', '/institute', '/timeline'].includes(i.to)) },
            { label: lang === 'en' ? 'Administration' : 'Administração', items: visible.filter(i => ['/manage-events', '/fx-rates', '/budgets', '/users', '/audit'].includes(i.to)) },
          ].filter(g => g.items.length > 0);

          return groups.map((group, gi) => (
            <div key={group.label}>
              {gi > 0 && <div style={{ height: 1, background: C.border, margin: '10px 4px' }} />}
              <div style={{ fontSize: 9, fontWeight: 700, color: '#5a7490', textTransform: 'uppercase', letterSpacing: '0.1em', padding: '6px 12px 4px' }}>
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={onNavClick}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '11px 12px', borderRadius: 10, textDecoration: 'none',
                    fontSize: 13, fontWeight: 600, transition: 'background 0.15s',
                    color: isActive ? C.onGold : C.soft,
                    background: isActive ? C.gold : 'transparent',
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon size={17} style={{ flexShrink: 0, opacity: isActive ? 1 : 0.85 }} />
                      <span>{item.label}</span>
                      {isActive && <TourButton compact />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ));
        })()}
      </nav>

      {/* User */}
      <div style={{ padding: '16px 8px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          {user?.photoURL
            ? <img src={user.photoURL} alt={user.name || ''} style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
            : <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.onGold, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                {user?.name?.[0] || user?.email?.[0]}
              </div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name || user?.email}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email}
            </div>
          </div>
          <LangToggle onLogout={onLogout} />
        </div>

      </div>
    </>
  );
}

export default function Layout() {
  const { user, signOut } = useAuth();
  const { t } = useI18n();
  const { mode } = useTheme();
  const [showLogout, setShowLogout] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();

  // Fecha drawer ao navegar
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <TourProvider>
    <div style={{ display: 'flex', height: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        @keyframes slideInLeft { from { transform: translateX(-100%) } to { transform: translateX(0) } }
        .logout-btn-cancel:hover { background: ${C.border} !important; color: #fff !important; }
        .logout-btn-confirm:hover { background: #b33a3a !important; }
        .hamburger-btn:hover { background: ${C.border} !important; }
        .sidebar-logout-btn:hover { background: rgba(192,57,43,0.25) !important; color: #ff6b55 !important; }
      `}</style>

      {showLogout && (
        <LogoutModal
          onConfirm={() => { setShowLogout(false); signOut(); }}
          onCancel={() => setShowLogout(false)}
        />
      )}

      {/* ── DESKTOP: sidebar fixa ── */}
      {!isMobile && (
        <aside style={{
          width: 272, minWidth: 272, background: C.card,
          borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column',
        }}>
          <SidebarContent user={user} onLogout={() => setShowLogout(true)} />
        </aside>
      )}

      {/* ── MOBILE: overlay + drawer deslizante ── */}
      {isMobile && drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
              animation: 'fadeIn 0.2s ease',
            }}
          />
          {/* Drawer */}
          <aside style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 201,
            width: 272, background: C.card,
            borderRight: `1px solid ${C.border}`,
            display: 'flex', flexDirection: 'column',
            animation: 'slideInLeft 0.22s ease',
            boxShadow: '4px 0 32px rgba(0,0,0,0.4)',
          }}>
            {/* Botão fechar */}
            <button
              onClick={() => setDrawerOpen(false)}
              style={{
                position: 'absolute', top: 16, right: 16, zIndex: 10,
                width: 32, height: 32, borderRadius: 8, border: 'none',
                background: C.border, color: C.muted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>
            <SidebarContent
              user={user}
              onLogout={() => { setDrawerOpen(false); setShowLogout(true); }}
              onNavClick={() => setDrawerOpen(false)}
            />
          </aside>
        </>
      )}

      {/* ── Conteúdo principal ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: `var(--bg,${C.bg})` }}>
        {/* Header mobile com hambúrguer */}
        {isMobile && (
          <header style={{
            height: 56, display: 'flex', alignItems: 'center',
            padding: '0 16px', gap: 14,
            background: C.card, borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}>
            <button
              className="hamburger-btn"
              onClick={() => setDrawerOpen(true)}
              style={{
                width: 40, height: 40, borderRadius: 10, border: 'none',
                background: 'transparent', color: C.muted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s',
              }}
            >
              <Menu size={22} />
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <img src={logoSrc(mode)} alt="GRI Institute" style={{ height: 20, width: 'auto', display: 'block' }} />
              <div style={{ fontSize: 9, color: C.goldText, letterSpacing: '0.07em', textTransform: 'uppercase', textAlign: 'left' }}>{t('app_title')}</div>
            </div>
          </header>
        )}

        <main style={{ flex: 1, overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>
    </div>
    </TourProvider>
  );
}
